// @flow

import * as preact from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { ToolWrapper, Error, Loading, getBaseUrl } from './tools-common';
import { useFetch } from '../utils/async';

type LetterGroup = {
	letter: string,
	values: Array<{ url: string, label: string }>,
};

type Contents = { az_nav?: { menu_items: Array<LetterGroup> } };

export function AZTool({ active }: { active: boolean }) {
	let dataUrl = `${getBaseUrl()}/wp-json/site-data/sidebar/a-z/`;
	let headers = { Accept: 'application/json' };
	let { data, error, isLoading } = useFetch<Contents>(dataUrl, { headers });

	// eslint-disable-next-line react-hooks/exhaustive-deps
	let menuItems = data?.az_nav?.menu_items || [];

	let letterState = useState<null | string>(null);
	let activeLetter = letterState[0];
	let setLetter = letterState[1];

	// when the data loads, set the "active letter" to the first letter in the response
	useEffect(() => {
		let firstLetter = menuItems[0]?.letter;
		setLetter(firstLetter);
	}, [menuItems, setLetter]);

	return (
		<ToolWrapper active={active} heading="A-Z Directory">
			{isLoading ? (
				<Loading />
			) : error ? (
				<Error error={error} />
			) : menuItems.length ? (
				<>
					<ul className="tools-nav__a-z-tab-list" role="tablist">
						{menuItems.map(group => (
							<AZButton
								activeLetter={activeLetter}
								group={group}
								onClick={() => setLetter(group.letter)}
							/>
						))}
					</ul>

					{menuItems.map(group => (
						<AZPage activeLetter={activeLetter} group={group} />
					))}
				</>
			) : null}
		</ToolWrapper>
	);
}

function AZButton(props: {
	activeLetter: ?string,
	onClick: () => mixed,
	group: LetterGroup,
}) {
	let { activeLetter, onClick, group } = props;
	let isActive = activeLetter === group.letter;

	let activeClass = isActive ? 'tools-nav__a-z-tab-trigger--active' : '';

	return (
		<li className="tools-nav__a-z-tab-item" role="presentation">
			<button
				className={`tools-nav__a-z-tab-trigger tools-nav--focusable ${activeClass}`}
				role="tab"
				aria-selected={isActive ? 'true' : 'false'}
				type="button"
				onClick={onClick}
			>
				<span className="tools-nav__a-z-tab-text">
					<span className="u-visual-hide">The letter </span>
					{group.letter}
				</span>
			</button>
		</li>
	);
}

function AZPage(props: { activeLetter: ?string, group: LetterGroup }) {
	let { activeLetter, group } = props;
	let isActive = activeLetter === group.letter;

	// focus the heading of a an a-z page when it becomes active
	let headingRef = useRef(null);
	useEffect(() => {
		headingRef.current && headingRef.current.focus();
	}, [isActive]);

	return (
		<div
			id={`tools-nav-a-z-${group.letter}`}
			className="tools-nav__a-z-container"
			role="tabpanel"
			style={{ display: isActive ? 'block' : 'none' }}
			aria-hidden={isActive ? 'true' : 'false'}
		>
			<h3 tabIndex="-1" className="u-visual-hide" ref={headingRef}>
				Departments starting with the letter {group.letter}
			</h3>

			<ul className="tools-nav__a-z-link-list">
				{group.values.map(link => (
					<li className="tools-nav__a-z-list-item">
						<a
							className="tools-nav__a-z-list-link tools-nav--focusable"
							href={link.url}
							dangerouslySetInnerHTML={{ __html: link.label }}
						/>
					</li>
				))}
			</ul>
		</div>
	);
}
